import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { crmErrorMessage } from "../api/crmErrors";
import useRole from "../hooks/useRole";
import { crmHref } from "../paths";
import CrmTooltip from "../ui/CrmTooltip";
import { cn } from "../../../lib/utils";
import { FOCUS_RING } from "../ui/classes";
import AddClientSheet from "../clients/AddClientSheet";
import { CRM } from "../../../constants/testIds/crm";

/**
 * Zero clients: the dashboard is replaced by a 3-card setup (spec "First-run
 * experience"): 1 Import your clients (opens the wizard), 2 Add first client (opens the
 * slide-over), 3 Turn on reminders (one tap, the fastest win). A tick marks each done.
 *
 * Turning reminders on is an owner action (sequences are owner-only); a helper sees the
 * card disabled with the reason.
 */
export default function FirstRunSetup({ setup, onEnableReminders, onChanged }) {
  const t = useT("crm");
  const tc = useT("common");
  const { can } = useRole();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const done = {
    import: !!setup.cards?.import?.done,
    add: !!setup.cards?.add_client?.done || (setup.client_count || 0) > 0,
    reminders: !!setup.cards?.reminders?.done,
  };

  const reminders = async () => {
    if (done.reminders || busy) return;
    setBusy(true);
    try {
      await onEnableReminders();
      toast.success(t("setup.remindersOn"));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const cards = [
    { key: "import", n: 1, title: t("setup.importTitle"), body: t("setup.importBody"), done: done.import, onClick: () => navigate(crmHref("/import")) },
    { key: "add", n: 2, title: t("setup.addTitle"), body: t("setup.addBody"), done: done.add, onClick: () => setAdding(true) },
    {
      key: "reminders", n: 3, title: t("setup.remindersTitle"), body: t("setup.remindersBody"), done: done.reminders,
      onClick: reminders, disabled: !can("manageEmail") || busy, tooltip: can("manageEmail") ? null : t("setup.remindersOwnerOnly"),
    },
  ];

  return (
    <section data-testid={CRM.setup} aria-labelledby="crm-setup-heading">
      <h2 id="crm-setup-heading" className="mb-4 font-display text-2xl text-eh-ink">{t("setup.title")}</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {cards.map((c) => {
          const card = (
            <button
              type="button"
              onClick={c.onClick}
              disabled={c.disabled}
              aria-disabled={c.disabled || undefined}
              className={cn("relative w-full rounded-2xl border border-eh-border-soft bg-eh-cream p-5 text-left hover:bg-eh-cream-deep disabled:cursor-not-allowed disabled:opacity-50", FOCUS_RING)}
            >
              <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-full bg-eh-ink text-sm font-semibold text-eh-bg">{c.n}</span>
              {c.done && (
                <span data-testid={CRM.setupCardDone(c.key)} role="img" aria-label={t("setup.done")}
                  className="absolute right-5 top-5 flex h-7 w-7 items-center justify-center rounded-full bg-eh-sage-deep text-eh-bg">
                  <Check size={16} aria-hidden="true" />
                </span>
              )}
              <span className="mt-3 block font-display text-xl text-eh-ink">{c.title}</span>
              <span className="mt-2 block text-sm text-eh-ink">{c.body}</span>
            </button>
          );
          return (
            <div key={c.key} data-testid={CRM.setupCard(c.key)} data-done={c.done ? "true" : "false"} className="[&>span]:flex [&>span]:w-full">
              {c.tooltip ? <CrmTooltip content={c.tooltip} testId={CRM.setupRemindersTooltip}>{card}</CrmTooltip> : card}
            </div>
          );
        })}
      </div>
      <AddClientSheet open={adding} onOpenChange={setAdding} onCreated={onChanged} />
    </section>
  );
}
